import * as RadixSwitch from '@radix-ui/react-switch';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Switch({
  checked,
  onCheckedChange,
  id,
  label,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  label: string;
  disabled?: boolean;
}) {
  return (
    <RadixSwitch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full border border-[color:var(--ts-input)] bg-muted',
        'transition-colors duration-150 ease-out hover:border-[color:var(--ts-input-hover)]',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary disabled:opacity-50',
      )}
    >
      <RadixSwitch.Thumb className="block h-3.5 w-3.5 translate-x-[3px] rounded-full bg-[color:var(--ts-surface)] transition-transform duration-150 ease-out data-[state=checked]:translate-x-[19px]" />
    </RadixSwitch.Root>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  id,
  label,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  id?: string;
  label: string;
}) {
  return (
    <RadixCheckbox.Root
      id={id}
      checked={checked}
      onCheckedChange={(next) => onCheckedChange(next === true)}
      aria-label={label}
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-[color:var(--ts-input)] bg-surface',
        'transition-colors duration-150 ease-out hover:border-[color:var(--ts-input-hover)]',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
      )}
    >
      <RadixCheckbox.Indicator>
        <Check className="h-3 w-3 text-primary-foreground" aria-hidden="true" />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
}

export interface RadioCardOption {
  value: string;
  title: string;
  description: string;
}

/**
 * Radio cards for the format choice on a new e-file. Hovering lifts a card off
 * the page; the chosen one takes a 2px navy border, a light blue fill and a
 * navy check in the corner.
 */
export function RadioCards({
  value,
  onValueChange,
  options,
  ariaLabel,
  className,
}: {
  value: string | null;
  onValueChange: (value: string) => void;
  options: RadioCardOption[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('grid gap-4 sm:grid-cols-2', className)}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'relative flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-left',
              'transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out',
              'hover:-translate-y-px hover:shadow-raised',
              active
                ? 'border-primary bg-[color:var(--ts-row-hover)]'
                : 'border-[color:var(--ts-border-soft)] bg-surface hover:border-[color:var(--ts-input-hover)]',
            )}
          >
            {active ? (
              <CheckCircle2
                className="absolute top-3 right-3 h-5 w-5 text-primary"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            ) : null}
            <span className="pr-8 text-lg font-medium text-gray-3">{option.title}</span>
            <span className="text-base text-muted-foreground">{option.description}</span>
          </button>
        );
      })}
    </div>
  );
}
